import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FormHelpers, getErrorSwal, showErrorSwal } from '@asamco/utils';
import { BehaviorSubject, Observable, of, Subject, timer } from 'rxjs';
import { catchError, filter, switchMap, takeUntil, tap } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { ApplicationLoginStatus } from '../../enums/application-login-status.enum';
import { AuthorizationStatus } from '../../enums/authorization-status.enum';
import { ApplicationLogin } from '../../interfaces/application-login.interface';
import { ApplicationLoginSelector, ApplicationLoginService } from '../../services/application-login.service';
import { AuthFlowModalComponent } from '@asamco/application-link/shared';
import { AuthFlowOutput } from '@asamco/application-link/shared';
import { FreshdeskHelpModalComponent } from '@asamco/application-link/shared';
import { DxiButtonModule } from 'devextreme-angular/ui/nested';
import { AsValidatorDirective } from '../../../../../../base/src/lib/as-validator/as-validator.directive';
import { DxTextBoxModule, DxNumberBoxModule, DxSelectBoxModule, DxCheckBoxModule, DxSwitchModule } from 'devextreme-angular';
import { NgIf, NgFor, NgSwitch, NgSwitchCase, NgClass, AsyncPipe, JsonPipe } from '@angular/common';
import { AuthFlowModalComponent as AuthFlowModalComponent_1 } from '../../../../../shared/src/lib/components/auth-flow-modal/auth-flow-modal.component';
import { ApplicationParams } from '../../interfaces/application-login-params.interface';
import notify from 'devextreme/ui/notify';
import { ApplicationLoginResult } from '../../interfaces/application-login-result.interface';

@Component({
    selector: 'al-link-edit-application-login-auth',
    templateUrl: './link-edit-application-login-auth.component.html',
    styleUrls: ['./link-edit-application-login-auth.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        AuthFlowModalComponent_1,
        NgIf,
        FormsModule,
        ReactiveFormsModule,
        NgFor,
        DxTextBoxModule,
        AsValidatorDirective,
        DxiButtonModule,
        DxNumberBoxModule,
        DxSelectBoxModule,
        NgSwitch,
        NgSwitchCase,
        NgClass,
        DxCheckBoxModule,
        AsyncPipe,
		DxSwitchModule,
		JsonPipe,
		FreshdeskHelpModalComponent
    ],
})
export class LinkEditApplicationLoginAuthComponent implements OnChanges, OnDestroy {
	@ViewChild(AuthFlowModalComponent)
	authFlowModal: AuthFlowModalComponent;

	@ViewChild('helpModal')
	helpModal?: FreshdeskHelpModalComponent;

	@Input()
	formGroup: FormGroup;

	@Input()
	linkId: number;

	//Either to or from
	@Input()
	appSide: string;

	@Input()
	fromLinkType: string;

	@Input()
	applicationId: number;

	@Input()
	selectedApplicationLoginId?: number;

	@Input()
	applicationName?: string;

	@Input()
	public appParams: ApplicationParams;

	@Input()
	saveAction: () => Observable<ApplicationLoginResult>;

	ApplicationLoginStatus = ApplicationLoginStatus;

	appLoginStatus: ApplicationLoginStatus = ApplicationLoginStatus.undefined;

	@Input()
	disabled: boolean;

	@Input()
	canManageApplicationLinkAdministration = false;

	@Input()
	disabledLoginBtnSubject = new Subject<boolean>();

	@Input()
	checkForm = false;

	public loginValiditySubject: BehaviorSubject<ApplicationLoginStatus> = new BehaviorSubject<ApplicationLoginStatus>(
		ApplicationLoginStatus.undefined
	);

	private destroy$: Subject<void> = new Subject<void>();

	private stopPolling$ = new Subject();

	isEncrypted: boolean = false;

	private applicationFreshdeskArticleIds: number[] = [];

	loginSelectors: ApplicationLoginSelector[] = [];
	loginSelectorsLoading = false;

	constructor(
		private appLoginService: ApplicationLoginService,
		private cdr: ChangeDetectorRef
	) {}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.applicationId || changes.appParams || changes.formGroup || changes.linkId || changes.selectedApplicationLoginId) {
			this.loadLoginSelectors(false);
		}

		if (!changes.applicationId || !this.applicationId) return;

		this.appLoginService.getApplicationArticles(this.applicationId)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: ids => {
					this.applicationFreshdeskArticleIds = (ids ?? [])
						.map(id => Number(id))
						.filter(id => Number.isFinite(id) && id > 0);
					this.cdr.markForCheck();
				},
				error: showErrorSwal,
			});
	}

	private get applicationLoginId(): number {
		return Number(this.selectedApplicationLoginId || this.formGroup?.get('id')?.value || 0);
	}

	loadLoginSelectors(showSelectionPrompt: boolean): void {
		if (!this.appParams?.supportsLoginSelectors || !this.applicationLoginId || !this.linkId) {
			this.resetLoginSelectors();
			return;
		}

		this.loginSelectorsLoading = true;
		this.cdr.markForCheck();

		this.appLoginService.getLoginSelectors(this.applicationLoginId, this.linkId)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: selectors => {
					this.loginSelectors = selectors ?? [];
					this.loginSelectorsLoading = false;
					this.patchSelectorFieldValues(this.loginSelectors);

					const selectorNeedingSelection = this.loginSelectors.find(selector => selector.options?.length > 1 && !selector.value && selector.requiredSelectionMessage);
					if (showSelectionPrompt && selectorNeedingSelection) {
						Swal.fire({
							icon: 'info',
							title: selectorNeedingSelection.label,
							text: selectorNeedingSelection.requiredSelectionMessage,
						});
					}

					this.cdr.markForCheck();
				},
				error: error => {
					this.loginSelectors = [];
					this.loginSelectorsLoading = false;
					this.cdr.markForCheck();
				},
			});
	}

	saveLoginSelector(selector: ApplicationLoginSelector, value: string): void {
		if (!selector?.key || !value || value === selector.value || !this.applicationLoginId || !this.linkId) {
			return;
		}

		this.disabledLoginBtnSubject.next(true);
		this.appLoginService.setLoginSelector(this.applicationLoginId, this.linkId, selector.key, value)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: selectors => {
					this.loginSelectors = selectors ?? [];
					this.patchSelectorFieldValues(this.loginSelectors);
					notify({
						message: `${selector.label} saved`,
						type: 'success',
						displayTime: 1000,
					});
					this.disabledLoginBtnSubject.next(false);
					this.cdr.markForCheck();
				},
				error: error => {
					showErrorSwal(error);
					this.disabledLoginBtnSubject.next(false);
					this.cdr.markForCheck();
				},
			});
	}

	private patchSelectorFieldValues(selectors: ApplicationLoginSelector[]): void {
		const fieldValues = (selectors ?? [])
			.map(selector => selector.fieldValues ?? {})
			.reduce((acc, values) => ({ ...acc, ...values }), {});

		if (Object.keys(fieldValues).length) {
			this.formGroup?.patchValue(fieldValues);
		}
	}

	private resetLoginSelectors(): void {
		this.loginSelectors = [];
		this.loginSelectorsLoading = false;
	}

	get freshdeskArticleIds(): number[] {
		const value = this.formGroup?.get('freshdeskArticleIds')?.value;

		if (Array.isArray(value)) {
			const ids = value
				.map(v => Number(v))
				.filter(v => Number.isFinite(v) && v > 0);
			return ids.length ? ids : this.applicationFreshdeskArticleIds;
		}

		if (typeof value === 'string') {
			const ids = value
				.split(/[\s,;]+/g)
				.map(v => v.trim())
				.filter(Boolean)
				.map(v => Number(v))
				.filter(v => Number.isFinite(v) && v > 0);
			return ids.length ? ids : this.applicationFreshdeskArticleIds;
		}


		return this.applicationFreshdeskArticleIds;
	}

	openIssueHelp(): void {
		const articleIds = this.freshdeskArticleIds;
		if (!articleIds.length) return;

		const title =
			(this.applicationName?.trim() ? `${this.applicationName.trim()} application login help` : null) ||
			this.formGroup?.get('name')?.value?.trim() ||
			`${(this.appSide ?? 'application').toUpperCase()} login help`;

		this.helpModal?.open(title, articleIds, {
			kind: 'application_login',
			linkId: this.linkId ?? null,
			appSide: this.appSide ?? null,
			applicationId: this.applicationId ?? null,
			loginName: this.formGroup?.get('name')?.value ?? null,
		});
	}

	public testbtn(): void {
		if (this.checkForm && !FormHelpers.validateForm(this.formGroup)) {
			this.loginValiditySubject.next(ApplicationLoginStatus.detailsinvalid);
		} else {
			this.disabledLoginBtnSubject.next(true);
			var login: ApplicationLogin = this.formGroup.value;
			this.loginValiditySubject.next(ApplicationLoginStatus.pending);

			let saveObservable = of(<ApplicationLoginResult>{});
			if(this.saveAction) {
				saveObservable = this.saveAction().pipe(tap(result => {
					this.formGroup.get('id').setValue(result.id);
					login.id = result.id;

					notify({
						message: 'Saving login changes to make testing possible',
						type: 'success',
						displayTime: 1000,
					});
				}));
			}

			// if necessary (save action is provided), save first so that input values come through in test
			saveObservable.pipe(switchMap(() => this.appLoginService.testLogin(this.linkId, this.appSide, login, this.fromLinkType.toLowerCase())))
			.subscribe({
				next: result => {
					this.disabledLoginBtnSubject.next(false);
					if (result) {
						this.loginValiditySubject.next(ApplicationLoginStatus.valid);
					} else {
						Swal.fire({ title: 'Unable to authenticate', icon: 'error' });
						this.loginValiditySubject.next(ApplicationLoginStatus.invalid);
					}
				},
				error: error => {
					this.disabledLoginBtnSubject.next(false);
					this.loginValiditySubject.next(ApplicationLoginStatus.undefined);

					const errorSwal = getErrorSwal(error);

					const redirectText = 'Redirect to ';
					const authFlowRequiredText = 'Auth flow step required: ';
					if (errorSwal.text.startsWith(redirectText)) {
						Swal.fire({ title: 'Authentication required', text: `Please use the 'auth' button to authenticate.`, icon: 'info' });
					} else if (errorSwal.text.startsWith(authFlowRequiredText)) {
						// remove authFlowRequiredText and ending dot
						const substr = errorSwal.text.substring(authFlowRequiredText.length, errorSwal.text.length - 1);
						const afo = JSON.parse(substr);

						this.authFlowModalStep(login, afo).subscribe(
							authFlowOutput => {
								if(this.loginValiditySubject.value !== ApplicationLoginStatus.valid) this.loginValiditySubject.next(ApplicationLoginStatus.invalid);
								this.disabledLoginBtnSubject.next(false);
							},
							err => {
								showErrorSwal(err);
								this.loginValiditySubject.next(ApplicationLoginStatus.invalid);
								this.disabledLoginBtnSubject.next(false);
							}
						);
					} else {
						Swal.fire(errorSwal);
					}
				}
			});
		}
	}

	public authorizebtn(): void {
		if (this.checkForm && !FormHelpers.validateForm(this.formGroup)) {
			this.loginValiditySubject.next(ApplicationLoginStatus.detailsinvalid);
		} else {
			this.disabledLoginBtnSubject.next(true);
			const login: ApplicationLogin = this.formGroup.value;
			if (!login.id) login.id = 0;

			let saveObservable = of(<ApplicationLoginResult>{});
			if(this.saveAction) {
				saveObservable = this.saveAction().pipe(tap(result => {
					this.formGroup.get('id').setValue(result.id);
					login.id = result.id;

					notify({
						message: 'Saving login changes to make authentication possible',
						type: 'success',
						displayTime: 1000,
					});
				}));
			}

			// if necessary (save action is provided), save first so that input values come through in auth
			saveObservable.pipe(
				// Step 1: Call authorize API
				switchMap(r =>
				  this.appLoginService.authorize(
					login,
					this.linkId,
					this.appSide,
					this.formGroup?.controls?.Dropdown?.value
				  ).pipe(
					// Step 2: Attempt to redirect, fallback to Swal link on failure
					tap(authResult => {
					  if (authResult && authResult.redirectUrl) {
						// Try to open the URL (may fail due to popup blockers)
						const openedWindow = window.open(authResult.redirectUrl, '_blank');

						// Check if blocked (null/undefined in some browsers)
						if (!openedWindow || openedWindow.closed) {
						  Swal.fire({
							title: 'Redirect Required',
							html: `
							  <p>Your browser blocked the automatic redirect.</p>
								Click 'Open Link' to authorize manually
							  </a>
							`,
							icon: 'warning',
							showConfirmButton: true,
							confirmButtonText: 'Open Link',
							showCancelButton: true,
							cancelButtonText: 'Cancel',
						  }).then((result) => {
							if (result.isConfirmed) {
							  window.open(authResult.redirectUrl, '_blank');
							}
						  });
						}
					  }
					}),
					// Step 3: Handle API errors
					catchError(error => {
					  showErrorSwal(error);
					  return of(error);
					})
				  )
				),
				// Step 4: Only proceed if auth succeeded
				filter(authResult => authResult.ok !== false),
				// Step 5: Start polling for auth status
				switchMap(authResult => {
				  this.formGroup.get('id').setValue(authResult.applicationLoginId);
				  return timer(0, 3000).pipe(
					switchMap(() => this.appLoginService.pollAuthorizeStatus(authResult.state)),
					takeUntil(this.stopPolling$)
				  );
				})
			  )
				.subscribe({
					next: e => {
						//Result should be true if the user is succesfully authenticated
						switch (e) {
							case AuthorizationStatus.Succesful:
								this.stopPolling$.next(undefined);
								Swal.fire({
									icon: 'success',
									title: 'Succesfully authenticated',
								});
								this.loadLoginSelectors(true);
								this.disabledLoginBtnSubject.next(false);
								break;
							case AuthorizationStatus.Error:
								this.stopPolling$.next(undefined);
								showErrorSwal('Error authenticating user')
								this.disabledLoginBtnSubject.next(false);
								break;
							case AuthorizationStatus.Connecting:
								break;
							default:
								this.disabledLoginBtnSubject.next(false);
								break;
						}
					},
					error: err => {
						showErrorSwal(err);
						this.disabledLoginBtnSubject.next(false);
					}
				});
		}
	}

	private authFlowStep(login, afInput) {
		return this.appLoginService.authFlow(login, afInput).pipe(switchMap(afo => this.authFlowModalStep(login, afo)));
	}

	private authFlowModalStep(login, authFlowOutput: AuthFlowOutput) {
		if (authFlowOutput.state === 'Successful') this.loginValiditySubject.next(ApplicationLoginStatus.valid);
		else if(authFlowOutput.state === 'Failed') this.loginValiditySubject.next(ApplicationLoginStatus.invalid);
		return this.authFlowModal
			.show(authFlowOutput)
			.pipe(
				switchMap(authFlowInput => {
					if(authFlowInput.selectedOption === 'Finish') {
						return of([]);
					} else {
						return this.authFlowStep(login, authFlowInput);
					}
				})
			);
	}

	ngOnDestroy(): void {
		this.stopPolling$.next(undefined);
		this.stopPolling$.complete();

		this.destroy$.next();
		this.destroy$.complete();
	}
}
